import { memo, type FC } from "react";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Chip from "@mui/material/Chip";
import IconButton from "@mui/material/IconButton";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import { LightTooltip } from "@/components/LightTooltip";
import { CsvUploadDropzone } from "../../../components/CsvUploadDropzone";

type UploadDropzoneProps = {
  onFilesAccepted: (files: File[]) => void;
};

export const UploadDropzone: FC<UploadDropzoneProps> = memo(
  ({ onFilesAccepted }) => {
    return (
      <CsvUploadDropzone
        id="uploaded-accounts-file"
        // Intentionally disabled: uploaded accounts CSV files can exceed 5MB.
        // This flow should not be blocked in the UI by a client-side size cap.
        disableMaxSize
        onFilesAccepted={onFilesAccepted}
        title={
          <Typography variant="body2" color="text.secondary" textAlign="center">
            Drag and drop your CSV file, or{" "}
            <Typography
              component="span"
              variant="body2"
              sx={{
                color: "primary.main",
                textDecoration: "underline",
                cursor: "pointer",
              }}
            >
              click here
            </Typography>{" "}
            to upload
          </Typography>
        }
      >
        <Box
          sx={{
            width: "100%",
            borderTop: 1,
            borderColor: "divider",
            pt: 1.5,
            mt: 0.5,
          }}
        >
          <Stack
            direction="row"
            spacing={0.5}
            alignItems="center"
            justifyContent="center"
            flexWrap="wrap"
            useFlexGap
            sx={{ rowGap: 0.5 }}
          >
            <Chip
              label="HG ID"
              size="small"
              variant="outlined"
              sx={{ height: 18, fontSize: 10 }}
            />
            <Chip
              label="DUNS"
              size="small"
              variant="outlined"
              sx={{ height: 18, fontSize: 10 }}
            />
            <Chip
              label="URL"
              size="small"
              variant="outlined"
              sx={{ height: 18, fontSize: 10 }}
            />
            <Chip
              label="Name"
              size="small"
              variant="outlined"
              sx={{ height: 18, fontSize: 10 }}
            />
            <Chip
              label="Country"
              size="small"
              variant="outlined"
              sx={{ height: 18, fontSize: 10 }}
            />
            <Chip
              label="CRM ID"
              size="small"
              variant="outlined"
              sx={{ height: 18, fontSize: 10 }}
            />
          </Stack>

          <Stack
            direction="row"
            spacing={0.5}
            alignItems="center"
            justifyContent="center"
            sx={{ mt: 1 }}
          >
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontSize: 10 }}
            >
              ↑ headers accepted (any order)
            </Typography>
            <LightTooltip
              title={
                <Box sx={{ p: 1 }}>
                  <Typography
                    variant="caption"
                    component="div"
                    sx={{ mb: 1.5 }}
                  >
                    <strong>HG Company ID</strong>
                    <br />
                    This is the HG-supplied identifier for a company. This field
                    has the highest priority when matching.
                  </Typography>
                  <Typography
                    variant="caption"
                    component="div"
                    sx={{ mb: 1.5 }}
                  >
                    <strong>DUNS</strong>
                    <br />
                    DUNS identifiers have the second-highest priority when
                    matching.
                  </Typography>
                  <Typography
                    variant="caption"
                    component="div"
                    sx={{ mb: 1.5 }}
                  >
                    <strong>URL</strong>
                    <br />
                    Company website URL used for matching.
                  </Typography>
                  <Typography
                    variant="caption"
                    component="div"
                    sx={{ mb: 1.5 }}
                  >
                    <strong>Company Name</strong>
                    <br />
                    Matched against all known names associated with a company.
                  </Typography>
                  <Typography
                    variant="caption"
                    component="div"
                    sx={{ mb: 1.5 }}
                  >
                    <strong>Country</strong>
                    <br />
                    Used alongside URL and/or company name to identify a company
                    within a specific region.
                  </Typography>
                  <Typography variant="caption" component="div">
                    <strong>CRM ID</strong>
                    <br />
                    Your CRM identifier (e.g., Salesforce ID). Not used for
                    matching but included in output.
                  </Typography>
                </Box>
              }
            >
              <IconButton size="small" sx={{ p: 0.25 }}>
                <HelpOutlineIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </LightTooltip>
          </Stack>
        </Box>
      </CsvUploadDropzone>
    );
  }
);

UploadDropzone.displayName = "UploadDropzone";
